import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Power, PowerOff, Bot, Users, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const AUTONOMY_LABELS = {
  manual: { label: "Manual approval", pill: "pill-neutral", icon: Users },
  assisted: { label: "Co-Pilot", pill: "pill-info", icon: Bot },
  autonomous: { label: "Autopilot", pill: "pill-success", icon: Bot },
};

const AGENT_META = {
  Hunter:   { color: "from-blue-500 to-cyan-500",    tag: "bg-blue-100 text-blue-700",    role: "Captures intent" },
  Closer:   { color: "from-violet-500 to-fuchsia-500", tag: "bg-violet-100 text-violet-700", role: "Closes deals" },
  Recovery: { color: "from-emerald-500 to-teal-600", tag: "bg-emerald-100 text-emerald-700", role: "Recovers revenue" },
  Insight:  { color: "from-amber-500 to-orange-500", tag: "bg-amber-100 text-amber-700",  role: "Surfaces decisions" },
  Growth:   { color: "from-rose-500 to-pink-500",    tag: "bg-rose-100 text-rose-700",    role: "Drives expansion" },
};

export default function Automations() {
  const [autos, setAutos] = useState([]);
  const [workers, setWorkers] = useState(null);
  const load = async () => {
    const [{ data: a }, { data: w }] = await Promise.all([
      api.get("/automations"),
      api.get("/automations/by-worker").catch(() => ({ data: null })),
    ]);
    setAutos(a);
    setWorkers(w);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (a) => {
    const status = a.status === "active" ? "paused" : "active";
    await api.patch(`/automations/${a.id}`, { status });
    toast.success(status === "active" ? "Resumed" : "Paused");
    load();
  };

  const changeAutonomy = async (a, mode) => {
    await api.patch(`/automations/${a.id}`, { autonomy: mode });
    load();
  };

  const emergencyStop = async () => {
    if (!window.confirm("Pause ALL automations immediately?")) return;
    await api.post("/automations/emergency-stop");
    toast.success("All automations paused");
    load();
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-6 lg:p-10" data-testid="automations-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-ink">Workforce</h1>
          <p className="text-sm text-meta mt-1">Your operational workers — keeping revenue moving 24/7. You stay in control.</p>
        </div>
        <button onClick={emergencyStop} className="rounded-full border border-rose-200 bg-danger-soft text-danger hover:bg-rose-100 px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 transition focus-ring" data-testid="emergency-stop">
          <ShieldAlert className="h-4 w-4" /> Emergency stop all
        </button>
      </div>

      <div className="mb-5 flex flex-wrap gap-2">
        {Object.entries(AGENT_META).map(([name, meta]) => (
          <span key={name} className={`pill ${meta.tag}`} data-testid={`agent-legend-${name}`}>
            <Bot className="h-3 w-3" /> {name} · <span className="font-normal opacity-80">{meta.role}</span>
          </span>
        ))}
      </div>

      {workers && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="worker-breakdown">
          {Object.entries(AGENT_META).map(([name, meta]) => {
            const w = workers[name] || { automations: 0, active: 0, runs_today: 0, revenue_generated: 0 };
            return (
              <div key={name} className="surface p-4" data-testid={`worker-card-${name}`}>
                <div className={`h-9 w-9 rounded-2xl bg-gradient-to-br ${meta.color} grid place-items-center shadow-sm mb-3`}>
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <p className="text-sm font-bold text-ink">{name}</p>
                <p className="text-[11px] text-meta">{meta.role}</p>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-meta">Runs today</p>
                    <p className="font-display text-base font-extrabold text-ink">{w.runs_today}</p>
                  </div>
                  <div>
                    <p className="text-meta">Revenue</p>
                    <p className="font-display text-base font-extrabold text-success">${Math.round(w.revenue_generated).toLocaleString()}</p>
                  </div>
                </div>
                <p className="mt-2 text-[11px] text-meta">{w.active}/{w.automations} active</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {autos.map((a) => {
          const meta = AUTONOMY_LABELS[a.autonomy] || AUTONOMY_LABELS.assisted;
          const I = meta.icon;
          const active = a.status === "active";
          const agent = AGENT_META[a.agent] || AGENT_META.Hunter;
          return (
            <div key={a.id} className="surface p-6 hover:shadow-md transition" data-testid={`auto-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`h-10 w-10 rounded-2xl bg-gradient-to-br ${agent.color} grid place-items-center shadow-sm shrink-0`}>
                    <Bot className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`pulse-dot ${active ? "" : "red"}`} />
                      <h3 className="font-display text-lg font-bold text-ink truncate">{a.name}</h3>
                    </div>
                    <p className="text-xs text-meta mt-1">{a.trigger}</p>
                  </div>
                </div>
                <span className={`pill ${meta.pill} shrink-0`}>
                  <I className="h-3 w-3" /> {meta.label}
                </span>
              </div>
              <p className="mt-3 text-sm text-ink-soft">{a.description}</p>
              <div className="mt-4 flex items-center gap-6 text-sm">
                <div>
                  <p className="text-[11px] text-meta uppercase tracking-wider font-semibold">Runs today</p>
                  <p className="font-display text-xl font-bold text-ink">{a.runs_today}</p>
                </div>
                <div>
                  <p className="text-[11px] text-meta uppercase tracking-wider font-semibold">Revenue</p>
                  <p className="font-display text-xl font-bold text-success">${Math.round(a.revenue_generated).toLocaleString()}</p>
                </div>
                <div className="ml-auto">
                  <span className={`pill ${active ? "pill-success" : "pill-neutral"}`}>{active ? "Active" : "Paused"}</span>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <button onClick={() => toggle(a)} className={`rounded-full px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 transition focus-ring ${active ? "bg-zinc-900 text-white hover:bg-zinc-800" : "bg-success-soft text-success hover:bg-emerald-200"}`} data-testid={`toggle-${a.id}`}>
                  {active ? <><PowerOff className="h-3 w-3" /> Pause</> : <><Power className="h-3 w-3" /> Resume</>}
                </button>
                {Object.keys(AUTONOMY_LABELS).map((k) => (
                  <button key={k} onClick={() => changeAutonomy(a, k)} className={`rounded-full px-3 py-1.5 text-xs font-medium border transition ${a.autonomy === k ? "border-violet-400 text-ai bg-ai-soft" : "border-line text-meta hover:text-ink hover:border-zinc-300"}`}>
                    {AUTONOMY_LABELS[k].label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
